import React, { useState, useEffect } from 'react';
import { 
  Filter, ChevronDown, Trophy, Target, Users, BarChart3,
  Activity, Zap
} from 'lucide-react';

// Import child components
import HeadToHeadChart from './HeadToHeadChart';
import StandingsComparison from './StandingsComparison';
import HeadToHeadBanner from './HeadToHeadBanner';

// Enhanced Manager Avatar Component with facing direction
const ManagerAvatar = ({ 
  managerName, 
  className = "", 
  fallbackIcon: FallbackIcon = Users, 
  clickable = false, 
  onClick,
  shouldFaceRight = false // New prop to control facing direction
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const imageSrc = `/${managerName.toLowerCase()}main.png`;
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  const handleClick = () => {
    if (clickable && onClick && !imageError) {
      onClick();
    }
  };
  
  // Apply horizontal flip transform if shouldFaceRight is true
  const imageTransform = shouldFaceRight ? 'scaleX(-1)' : 'scaleX(1)';
  
  if (imageError || !imageSrc) {
    return (
      <div className={`bg-white/20 rounded-full flex items-center justify-center ${className} ${clickable ? 'cursor-pointer' : ''}`}>
        <FallbackIcon className="h-6 w-6" />
      </div>
    );
  }
  
  return (
    <div 
      className={`relative rounded-full overflow-hidden ${className} ${clickable ? 'cursor-pointer hover:scale-105 transition-transform duration-200' : ''}`}
      onClick={handleClick}
    >
      <img
        src={imageSrc}
        alt={`${managerName} profile`}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          transform: imageTransform,
          transition: 'opacity 0.3s ease, transform 0.2s ease'
        }}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
      {!imageLoaded && (
        <div className="absolute inset-0 bg-white/20 rounded-full flex items-center justify-center">
          <FallbackIcon className="h-6 w-6" />
        </div>
      )}
      {clickable && imageLoaded && (
        <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
          <div className="bg-white/90 rounded-full p-2">
            <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM12 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

// Utility Components
const StatCard = ({ title, value, subtitle, Icon, color = "purple" }) => {
  const colorClasses = {
    purple: "from-purple-500 to-purple-600",
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    orange: "from-orange-500 to-orange-600",
    red: "from-red-500 to-red-600"
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm lg:text-base font-medium text-gray-600">{title}</p>
          <p className="text-xl lg:text-3xl font-bold text-gray-800 mt-1">{value}</p>
          {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`p-3 rounded-lg bg-gradient-to-br ${colorClasses[color]} text-white`}>
            <Icon className="h-6 w-6" />
          </div>
        )}
      </div>
    </div>
  );
};

const LoadingSpinner = ({ message = "Loading data..." }) => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
    <span className="ml-3 text-lg text-gray-600">{message}</span>
  </div>
);

const ErrorMessage = ({ error }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
    <h3 className="text-red-800 font-semibold">Error loading data</h3>
    <p className="text-red-600 mt-2">{error}</p>
  </div>
);

// Add CSS styles for the range slider
const sliderStyles = `
  .range-slider-container {
    position: relative;
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    margin: 10px 0;
  }
  
  .range-slider-track {
    position: absolute;
    height: 8px;
    background: linear-gradient(to right, #8b5cf6, #a855f7);
    border-radius: 4px;
    top: 0;
  }
  
  .range-slider {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    position: absolute;
    top: -6px;
    height: 20px;
    width: 100%;
    outline: none;
  }
  
  .range-slider::-webkit-slider-track {
    background: transparent;
    height: 20px;
  }
  
  .range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #8b5cf6;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    border: 3px solid white;
    transition: all 0.2s ease;
  }
  
  .range-slider::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }
  
  .range-slider::-moz-range-track {
    background: transparent;
    height: 20px;
    border: none;
  }
  
  .range-slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #8b5cf6;
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }
`;

// Helper function to get random managers (excluding Sam)
const getRandomManagers = (managersArray) => {
  const eligibleManagers = managersArray.filter(manager => manager !== 'Sam');
  const shuffled = [...eligibleManagers].sort(() => 0.5 - Math.random());
  return [shuffled[0], shuffled[1]];
};

// Main HeadToHead Component
const HeadToHead = ({ h2hData, finalData, loading, error, managers }) => {
  // Initialize with empty strings, will be set by useEffect
  const [selectedManager1, setSelectedManager1] = useState('');
  const [selectedManager2, setSelectedManager2] = useState('');
  const [selectedGameTypes, setSelectedGameTypes] = useState(['Regular Season', 'Playoffs', 'Consolation']);
  const [yearRange, setYearRange] = useState([2012, 2023]);
  const [modalImage, setModalImage] = useState(null);

  // Randomize managers on component mount
  useEffect(() => {
    if (managers && managers.length >= 2) {
      const [randomManager1, randomManager2] = getRandomManagers(managers);
      setSelectedManager1(randomManager1);
      setSelectedManager2(randomManager2);
    }
  }, [managers]);

  // Modal functions
  const openImageModal = (managerName) => {
    setModalImage({
      src: `/${managerName.toLowerCase()}main.png`,
      alt: `${managerName} profile image`,
      name: managerName
    });
  };

  const closeImageModal = () => {
    setModalImage(null);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeImageModal();
      }
    };

    if (modalImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [modalImage]);

  // Inject styles
  useEffect(() => {
    if (!document.getElementById('slider-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'slider-styles';
      styleSheet.textContent = sliderStyles;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // Get min and max years from the actual data
  const getYearRange = () => {
    if (!h2hData.length) return { minYear: 2012, maxYear: 2023 };
    
    const years = h2hData.map(game => game.Year).filter(year => year && !isNaN(year));
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    
    return { minYear, maxYear };
  };

  const { minYear, maxYear } = getYearRange();

  // Update year range when data loads
  useEffect(() => {
    if (h2hData.length > 0) {
      const { minYear, maxYear } = getYearRange();
      setYearRange([minYear, maxYear]);
    }
  }, [h2hData.length]);

  // Core data processing functions
  const getH2HData = (manager1, manager2, gameTypes, yearRange) => {
    if (!h2hData.length) return [];
    
    // Get all games between these two managers (bidirectional)
    const filteredData = h2hData.filter(game => 
      ((game.Manager1 === manager1 && game.Manager2 === manager2) ||
       (game.Manager1 === manager2 && game.Manager2 === manager1)) &&
      gameTypes.includes(game.Type) &&
      game.Year >= yearRange[0] && game.Year <= yearRange[1]
    );
    
    // Create unique games (deduplicate bidirectional entries)
    const gameMap = new Map();
    
    filteredData.forEach(game => {
      const gameId = `${Math.min(game.Week, game.Week)}-${game.Year}-${game.Type}`;
      
      if (!gameMap.has(gameId)) {
        gameMap.set(gameId, game);
      }
    });
    
    const uniqueData = Array.from(gameMap.values());
    
    // Transform into chart format with consistent manager ordering
    const uniqueGames = uniqueData.map((game, index) => {
      // Create a consistent unique ID based on content
      const gameId = game.Week * 10000 + game.Year + (game.Type === 'Playoffs' ? 1000 : game.Type === 'Consolation' ? 2000 : 0);
      
      const manager1Instance = game.Manager1 === manager1 ? game : h2hData.find(g => 
        g.Manager1 === manager1 && g.Manager2 === manager2 && 
        g.Week === game.Week && g.Year === game.Year && g.Type === game.Type
      ) || game;
      
      return {
        gameId,
        week: `${game.Year} - Week ${game.Week}`,
        type: game.Type,
        [manager1]: manager1Instance.Manager1 === manager1 ? manager1Instance.PF : manager1Instance.PA,
        [manager2]: manager1Instance.Manager1 === manager2 ? manager1Instance.PF : manager1Instance.PA,
        outcome1: manager1Instance.Manager1 === manager1 ? manager1Instance.Outcome : (manager1Instance.Outcome === 'Win' ? 'Loss' : 'Win'),
        outcome2: manager1Instance.Manager1 === manager2 ? manager1Instance.Outcome : (manager1Instance.Outcome === 'Win' ? 'Loss' : 'Win')
      };
    });
    
    return uniqueGames.sort((a, b) => a.gameId - b.gameId);
  };
  
  const getH2HStats = (manager1, manager2, gameTypes, yearRange) => {
    if (!h2hData.length) return { 
      winPct: 0, 
      avgPF: 0, 
      avgMargin: 0 
    };
    
    // Get all head-to-head games between these two managers from manager1's perspective
    const h2hGames = h2hData.filter(game => 
      game.Manager1 === manager1 && 
      game.Manager2 === manager2 && 
      gameTypes.includes(game.Type) &&
      game.Year >= yearRange[0] && game.Year <= yearRange[1]
    );
    
    if (h2hGames.length === 0) return { winPct: 0, avgPF: 0, avgMargin: 0 };
    
    const wins = h2hGames.filter(game => game.Outcome === 'Win').length;
    const winPct = wins / h2hGames.length;
    const avgPF = h2hGames.reduce((sum, game) => sum + (game.PF || 0), 0) / h2hGames.length;
    
    // Calculate average margin for wins only
    const winGames = h2hGames.filter(game => game.Outcome === 'Win');
    const avgMargin = winGames.length > 0 ?
      winGames.reduce((sum, game) => sum + (game.MoV || 0), 0) / winGames.length : 0;
    
    return { winPct, avgPF, avgMargin };
  };

  // Calculate H2H game streaks for both managers
  const calculateH2HStreaks = (manager1, manager2, gameTypes, yearRange) => {
    if (!h2hData.length) return { 
      [manager1]: { currentStreak: 0, currentType: null, longestStreak: 0, totalStreaks: 0 },
      [manager2]: { currentStreak: 0, currentType: null, longestStreak: 0, totalStreaks: 0 }
    };

    // Get all head-to-head games in chronological order
    const h2hGames = h2hData.filter(game => 
      game.Manager1 === manager1 && 
      game.Manager2 === manager2 && 
      gameTypes.includes(game.Type) &&
      game.Year >= yearRange[0] && game.Year <= yearRange[1]
    ).sort((a, b) => {
      if (a.Year !== b.Year) return a.Year - b.Year;
      return a.Week - b.Week;
    });

    if (h2hGames.length === 0) return { 
      [manager1]: { currentStreak: 0, currentType: null, longestStreak: 0, totalStreaks: 0 },
      [manager2]: { currentStreak: 0, currentType: null, longestStreak: 0, totalStreaks: 0 }
    };

    // Calculate streaks for manager1
    const calculateManagerStreaks = (targetManager, isManager1 = true) => {
      let currentStreak = 0;
      let currentType = null;
      let longestStreak = 0;
      let totalStreaks = 0;
      let tempStreak = 0;
      let tempType = null;

      const gamesToAnalyze = isManager1 ? h2hGames : h2hGames.map(game => ({
        ...game,
        Outcome: game.Outcome === 'Win' ? 'Loss' : 'Win'
      }));

      gamesToAnalyze.forEach((game, index) => {
        const isWin = game.Outcome === 'Win';
        const gameType = isWin ? 'win' : 'loss';

        if (tempType === null) {
          tempType = gameType;
          tempStreak = 1;
        } else if (tempType === gameType) {
          tempStreak++;
        } else {
          // Streak ended, count it if it's 2+ games
          if (tempStreak >= 2 && tempType === 'win') {
            totalStreaks++;
            longestStreak = Math.max(longestStreak, tempStreak);
          }
          tempType = gameType;
          tempStreak = 1;
        }

        // Update current streak (if it's the most recent)
        if (index === gamesToAnalyze.length - 1) {
          currentStreak = tempStreak;
          currentType = tempType;
        }
      });

      // Final streak check
      if (tempStreak >= 2 && tempType === 'win') {
        totalStreaks++;
        longestStreak = Math.max(longestStreak, tempStreak);
      }

      return { currentStreak, currentType, longestStreak, totalStreaks };
    };

    return {
      [manager1]: calculateManagerStreaks(manager1, true),
      [manager2]: calculateManagerStreaks(manager2, false)
    };
  };

  // Handle loading and error states
  if (loading) return <LoadingSpinner message="Loading head-to-head data..." />;
  if (error) return <ErrorMessage error={error} />;

  // Don't render if managers aren't selected yet
  if (!selectedManager1 || !selectedManager2) {
    return <LoadingSpinner message="Selecting random managers..." />;
  }

  // Process data for current selections
  const h2hChartData = getH2HData(selectedManager1, selectedManager2, selectedGameTypes, yearRange);
  
  // Get head-to-head wins using the correct method
  const manager1PerspectiveGames = h2hData.filter(game => 
    game.Manager1 === selectedManager1 && 
    game.Manager2 === selectedManager2 && 
    selectedGameTypes.includes(game.Type) &&
    game.Year >= yearRange[0] && game.Year <= yearRange[1]
  );
  
  const manager1Wins = manager1PerspectiveGames.filter(game => game.Outcome === 'Win').length;
  const manager1Losses = manager1PerspectiveGames.filter(game => game.Outcome === 'Loss').length;
  const manager2Wins = manager1Losses;
  
  // Get stats for each manager in their head-to-head matchups
  const manager1Stats = getH2HStats(selectedManager1, selectedManager2, selectedGameTypes, yearRange);
  const manager2Stats = getH2HStats(selectedManager2, selectedManager1, selectedGameTypes, yearRange);

  // Calculate H2H streaks
  const h2hStreaks = calculateH2HStreaks(selectedManager1, selectedManager2, selectedGameTypes, yearRange);

  // Calculate standings comparison for the cards
  const getStandingsComparison = () => {
    if (!finalData.length) return { manager1Better: 0, manager2Better: 0, totalYears: 0 };

    // Filter final data for both managers within the year range
    const manager1Data = finalData.filter(row => 
      row.Owner === selectedManager1 && 
      row.Year >= yearRange[0] && 
      row.Year <= yearRange[1]
    );
    
    const manager2Data = finalData.filter(row => 
      row.Owner === selectedManager2 && 
      row.Year >= yearRange[0] && 
      row.Year <= yearRange[1]
    );

    // Create yearly comparison
    const yearlyComparison = {};
    
    manager1Data.forEach(row => {
      yearlyComparison[row.Year] = { ...yearlyComparison[row.Year], [selectedManager1]: row.Standings };
    });

    manager2Data.forEach(row => {
      yearlyComparison[row.Year] = { ...yearlyComparison[row.Year], [selectedManager2]: row.Standings };
    });

    // Calculate head-to-head standings comparison
    let manager1Better = 0;
    let manager2Better = 0;
    let totalYears = 0;

    Object.values(yearlyComparison).forEach(year => {
      const manager1Standing = year[selectedManager1];
      const manager2Standing = year[selectedManager2];
      
      if (manager1Standing && manager2Standing) {
        totalYears++;
        if (manager1Standing < manager2Standing) {
          manager1Better++;
        } else if (manager2Standing < manager1Standing) {
          manager2Better++;
        }
      }
    });

    return { manager1Better, manager2Better, totalYears };
  };

  const standingsComparison = getStandingsComparison();

  return (
    <div className="space-y-8">
      {/* Banner Header */}
      <HeadToHeadBanner />

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-2xl max-h-[90vh] w-full">
            <button
              onClick={closeImageModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl font-bold bg-black/50 rounded-full w-10 h-10 flex items-center justify-center z-10 transition-colors duration-200"
            >
              ×
            </button>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-2xl">
              <img
                src={modalImage.src}
                alt={modalImage.alt}
                className="w-full h-full object-contain rounded-xl max-h-[80vh]"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="text-center mt-4">
                <h3 className="text-xl font-bold text-white">{modalImage.name}</h3>
                <p className="text-white/70 text-sm">Manager Profile Image</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Controls Section with Better Visual Hierarchy */}
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 lg:p-10">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl text-white shadow-lg">
              <Filter className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-800">Filter Controls</h2>
              <p className="text-gray-600 text-sm lg:text-base">Customize your head-to-head analysis</p>
            </div>
          </div>
          
          {/* Quick Stats Badge */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-2 text-center">
              <div className="text-lg font-bold text-purple-600">{h2hChartData.length}</div>
              <div className="text-xs text-purple-700">Total Games</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 text-center">
              <div className="text-lg font-bold text-blue-600">{yearRange[1] - yearRange[0] + 1}</div>
              <div className="text-xs text-blue-700">Seasons</div>
            </div>
          </div>
        </div>
  
        {/* Filter Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8">
          
          {/* Manager 1 Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <label className="text-sm font-bold text-gray-800 uppercase tracking-wide">Manager 1</label>
            </div>
            <div className="relative">
              <select 
                value={selectedManager1}
                onChange={(e) => setSelectedManager1(e.target.value)}
                className="w-full p-4 bg-white/70 border-2 border-purple-200 rounded-xl focus:ring-4 focus:ring-purple-500/30 focus:border-purple-500 backdrop-blur-sm font-semibold text-base transition-all duration-300 hover:border-purple-400 appearance-none cursor-pointer"
              >
                {managers.map(manager => (
                  <option key={manager} value={manager}>{manager}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-500 pointer-events-none" />
            </div>
            <div className="text-xs text-gray-500 pl-2">Primary comparison manager</div>
          </div>
          
          {/* Manager 2 Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <label className="text-sm font-bold text-gray-800 uppercase tracking-wide">Manager 2</label>
            </div>
            <div className="relative">
              <select 
                value={selectedManager2}
                onChange={(e) => setSelectedManager2(e.target.value)}
                className="w-full p-4 bg-white/70 border-2 border-blue-200 rounded-xl focus:ring-4 focus:ring-blue-500/30 focus:border-blue-500 backdrop-blur-sm font-semibold text-base transition-all duration-300 hover:border-blue-400 appearance-none cursor-pointer"
              >
                {managers.map(manager => (
                  <option key={manager} value={manager}>{manager}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-500 pointer-events-none" />
            </div>
            <div className="text-xs text-gray-500 pl-2">Secondary comparison manager</div>
          </div>
  
          {/* Game Types Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"></div>
              <label className="text-sm font-bold text-gray-800 uppercase tracking-wide">Game Types</label>
            </div>
            <div className="bg-white/50 border-2 border-gray-200 rounded-xl p-4 space-y-3">
              {['Regular Season', 'Playoffs', 'Consolation'].map((gameType, index) => (
                <label key={gameType} className="flex items-center group cursor-pointer">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={selectedGameTypes.includes(gameType)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGameTypes([...selectedGameTypes, gameType]);
                        } else {
                          setSelectedGameTypes(selectedGameTypes.filter(type => type !== gameType));
                        }
                      }}
                      className="sr-only" 
                    />
                    <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 ${
                      selectedGameTypes.includes(gameType) 
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-green-500' 
                        : 'border-gray-300 group-hover:border-green-400'
                    }`}>
                      {selectedGameTypes.includes(gameType) && (
                        <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                    {gameType}
                  </span>
                </label>
              ))}
            </div>
            <div className="text-xs text-gray-500 pl-2">Select game types to include</div>
          </div>
  
          {/* Year Range Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-3 h-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-full"></div>
              <label className="text-sm font-bold text-gray-800 uppercase tracking-wide">Year Range</label>
            </div>
            <div className="bg-white/50 border-2 border-gray-200 rounded-xl p-4">
              <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                <span>{yearRange[0]}</span>
                <span>{yearRange[1]}</span>
              </div>
              <div className="range-slider-container">
                <div 
                  className="range-slider-track"
                  style={{
                    left: `${((yearRange[0] - minYear) / (maxYear - minYear)) * 100}%`,
                    width: `${((yearRange[1] - yearRange[0]) / (maxYear - minYear)) * 100}%`
                  }}
                ></div>
                <input
                  type="range"
                  min={minYear}
                  max={maxYear}
                  value={yearRange[0]}
                  onChange={(e) => setYearRange([parseInt(e.target.value), yearRange[1]])}
                  className="range-slider"
                  style={{ zIndex: 2 }}
                />
                <input
                  type="range"
                  min={minYear}
                  max={maxYear}
                  value={yearRange[1]}
                  onChange={(e) => setYearRange([yearRange[0], parseInt(e.target.value)])}
                  className="range-slider"
                  style={{ zIndex: 1 }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{minYear}</span>
                <span>{maxYear}</span>
              </div>
            </div>
            <div className="text-xs text-gray-500 pl-2">Adjust analysis time period</div>
          </div>
        </div>
      </div>

      {/* Head-to-Head Results Cards with Facing Images */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Manager 1 Card - LEFT SIDE (faces right) */}
        <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 rounded-3xl shadow-2xl p-6 lg:p-8 text-white relative overflow-hidden min-h-[500px]">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          
          {/* Large Background Avatar - Manager 1 faces RIGHT */}
          <div 
            className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 opacity-20 cursor-pointer hover:opacity-30 transition-opacity duration-200"
            onClick={() => openImageModal(selectedManager1)}
          >
            <ManagerAvatar 
              managerName={selectedManager1} 
              className="w-56 h-56 lg:w-72 lg:h-72"
              shouldFaceRight={true}
            />
          </div>
          
          <div className="relative z-10">
            {/* Header with Larger Foreground Avatar and Name - Manager 1 faces RIGHT */}
            <div className="flex items-center space-x-4 mb-8">
              <ManagerAvatar 
                managerName={selectedManager1} 
                className="w-20 h-20 lg:w-24 lg:h-24 shadow-xl"
                clickable={true}
                onClick={() => openImageModal(selectedManager1)}
                shouldFaceRight={true}
              />
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold">{selectedManager1}</h3>
                <p className="text-purple-100 text-sm lg:text-base opacity-90">Manager Profile</p>
              </div>
            </div>
            
            {/* Large Win Count */}
            <div className="bg-white/10 rounded-2xl p-6 mb-4 text-center backdrop-blur-sm">
              <div className="text-6xl lg:text-8xl font-bold opacity-90">{manager1Wins}</div>
              <div className="text-lg text-purple-100">Wins vs {selectedManager2}</div>
            </div>

            {/* Standings Comparison */}
            <div className="bg-white/10 rounded-2xl p-4 mb-6 text-center backdrop-blur-sm">
              <div className="text-4xl lg:text-5xl font-bold">{standingsComparison.manager1Better}</div>
              <div className="text-sm text-purple-100">Higher Standings Finishes</div>
            </div>

            {/* H2H Streak Information */}
            {h2hStreaks[selectedManager1] && (
              <div className="bg-white/10 rounded-2xl p-4 mb-6 backdrop-blur-sm">
                <h4 className="text-sm text-purple-100 mb-3 font-semibold">H2H Game Streaks</h4>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold">
                      {h2hStreaks[selectedManager1].currentStreak}{h2hStreaks[selectedManager1].currentType === 'win' ? 'W' : 'L'}
                    </div>
                    <div className="text-xs text-purple-100">
                      Current {h2hStreaks[selectedManager1].currentType === 'win' ? '🔥' : '❄️'}
                    </div>
                  </div>
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold">{h2hStreaks[selectedManager1].longestStreak}</div>
                    <div className="text-xs text-purple-100">Best Streak</div>
                  </div>
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold">{h2hStreaks[selectedManager1].totalStreaks}</div>
                    <div className="text-xs text-purple-100">Total Streaks</div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-black/20 rounded-xl p-3 lg:p-4 text-center backdrop-blur-sm">
                <div className="text-lg lg:text-xl font-bold">{(manager1Stats.winPct * 100).toFixed(1)}%</div>
                <div className="text-xs text-purple-100">Win Pct</div>
              </div>
              <div className="bg-black/20 rounded-xl p-3 lg:p-4 text-center backdrop-blur-sm">
                <div className="text-lg lg:text-xl font-bold">{manager1Stats.avgPF.toFixed(1)}</div>
                <div className="text-xs text-purple-100">Avg PF</div>
              </div>
              <div className="bg-black/20 rounded-xl p-3 lg:p-4 text-center backdrop-blur-sm">
                <div className="text-lg lg:text-xl font-bold">{manager1Stats.avgMargin.toFixed(1)}</div>
                <div className="text-xs text-purple-100">Avg Margin</div>
              </div>
            </div>
          </div>
        </div>

        {/* Manager 2 Card - RIGHT SIDE (faces left - normal orientation) */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-800 rounded-3xl shadow-2xl p-6 lg:p-8 text-white relative overflow-hidden min-h-[500px]">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          
          {/* Large Background Avatar - Manager 2 faces LEFT (normal) */}
          <div 
            className="absolute -top-4 -left-4 lg:-top-6 lg:-left-6 opacity-20 cursor-pointer hover:opacity-30 transition-opacity duration-200"
            onClick={() => openImageModal(selectedManager2)}
          >
            <ManagerAvatar 
              managerName={selectedManager2} 
              className="w-56 h-56 lg:w-72 lg:h-72"
              shouldFaceRight={false}
            />
          </div>
          
          <div className="relative z-10">
            {/* Header with Larger Foreground Avatar and Name - Manager 2 faces LEFT (normal) */}
            <div className="flex items-center space-x-4 mb-8">
              <ManagerAvatar 
                managerName={selectedManager2} 
                className="w-20 h-20 lg:w-24 lg:h-24 shadow-xl"
                clickable={true}
                onClick={() => openImageModal(selectedManager2)}
                shouldFaceRight={false}
              />
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold">{selectedManager2}</h3>
                <p className="text-blue-100 text-sm lg:text-base opacity-90">Manager Profile</p>
              </div>
            </div>
            
            {/* Large Win Count */}
            <div className="bg-white/10 rounded-2xl p-6 mb-4 text-center backdrop-blur-sm">
              <div className="text-6xl lg:text-8xl font-bold opacity-90">{manager2Wins}</div>
              <div className="text-lg text-blue-100">Wins vs {selectedManager1}</div>
            </div>

            {/* Standings Comparison */}
            <div className="bg-white/10 rounded-2xl p-4 mb-6 text-center backdrop-blur-sm">
              <div className="text-4xl lg:text-5xl font-bold">{standingsComparison.manager2Better}</div>
              <div className="text-sm text-blue-100">Higher Standings Finishes</div>
            </div>

            {/* H2H Streak Information */}
            {h2hStreaks[selectedManager2] && (
              <div className="bg-white/10 rounded-2xl p-4 mb-6 backdrop-blur-sm">
                <h4 className="text-sm text-blue-100 mb-3 font-semibold">H2H Game Streaks</h4>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold">
                      {h2hStreaks[selectedManager2].currentStreak}{h2hStreaks[selectedManager2].currentType === 'win' ? 'W' : 'L'}
                    </div>
                    <div className="text-xs text-blue-100">
                      Current {h2hStreaks[selectedManager2].currentType === 'win' ? '🔥' : '❄️'}
                    </div>
                  </div>
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold">{h2hStreaks[selectedManager2].longestStreak}</div>
                    <div className="text-xs text-blue-100">Best Streak</div>
                  </div>
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-lg font-bold">{h2hStreaks[selectedManager2].totalStreaks}</div>
                    <div className="text-xs text-blue-100">Total Streaks</div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-black/20 rounded-xl p-3 lg:p-4 text-center backdrop-blur-sm">
                <div className="text-lg lg:text-xl font-bold">{(manager2Stats.winPct * 100).toFixed(1)}%</div>
                <div className="text-xs text-blue-100">Win Pct</div>
              </div>
              <div className="bg-black/20 rounded-xl p-3 lg:p-4 text-center backdrop-blur-sm">
                <div className="text-lg lg:text-xl font-bold">{manager2Stats.avgPF.toFixed(1)}</div>
                <div className="text-xs text-blue-100">Avg PF</div>
              </div>
              <div className="bg-black/20 rounded-xl p-3 lg:p-4 text-center backdrop-blur-sm">
                <div className="text-lg lg:text-xl font-bold">{manager2Stats.avgMargin.toFixed(1)}</div>
                <div className="text-xs text-blue-100">Avg Margin</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Head-to-Head Chart */}
      <HeadToHeadChart 
        chartData={h2hChartData}
        manager1Name={selectedManager1}
        manager2Name={selectedManager2}
        gameCount={h2hChartData.length}
      />

      {/* Standings Comparison */}
      <StandingsComparison 
        finalData={finalData}
        manager1Name={selectedManager1}
        manager2Name={selectedManager2}
        yearRange={yearRange}
        className="mt-8"
      />
    </div>
  );
};

export default HeadToHead;